import { useEffect } from 'react';

/**
 * useRemoveStickyOnIntro
 * Temporarily removes sticky positioning (sets to static) for elements matching `selector`
 * that contain a descendant with a data-intro attribute while `active` is true.
 * Restores the original inline position (or sticky) when `active` becomes false.
 * Observes the DOM only while active to catch dynamically added data-intro attributes.
 *
 * @param {string} selector CSS selector for candidate elements, e.g. '.rc-accordion-header'
 * @param {boolean} active Typically the tour active flag.
 * @param {object} [options]
 * @param {string} [options.overridePosition='static'] The position to apply while disabling sticky.
 * @param {boolean} [options.useImportant=true] Apply !important when overriding.
 */
export function useRemoveStickyOnIntro(
  selector,
  active,
  { overridePosition = 'static', useImportant = true } = {}
) {
  useEffect(() => {
    if (!active) {
      // On transition to inactive, restore any previously modified elements
      const headers = document.querySelectorAll(selector);
      headers.forEach(h => {
        if (h.dataset.stickyRemoved) {
          if (h.dataset.origInlinePos === '') h.style.removeProperty('position');
          else h.style.position = h.dataset.origInlinePos || 'sticky';
          delete h.dataset.stickyRemoved;
          delete h.dataset.origInlinePos;
        }
      });
      return; // No observer when inactive
    }

    const apply = () => {
      const nodes = document.querySelectorAll(selector);
      nodes.forEach(el => {
        const hasIntro = el.querySelector('[data-intro]');
        if (hasIntro) {
          const cs = window.getComputedStyle(el);
          if (cs.position === 'sticky' && !el.dataset.stickyRemoved) {
            el.dataset.stickyRemoved = '1';
            el.dataset.origInlinePos = el.style.position || '';
            el.style.setProperty('position', overridePosition, useImportant ? 'important' : '');
          }
        }
      });
    };

    apply();

    let scheduled = false;
    const scheduleApply = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        apply();
      });
    };

    const mo = new MutationObserver(mutations => {
      // Any mutation we are observing (attribute change, added/removed nodes) is relevant; coalesce via rAF.
      if (mutations && mutations.length) scheduleApply();
    });

    mo.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-intro']
    });

    return () => {
      mo.disconnect();
      // Restore when effect unmounts (e.g., component unmount or active -> false re-run path already handled above)
      const nodes = document.querySelectorAll(selector);
      nodes.forEach(el => {
        if (el.dataset.stickyRemoved) {
          if (el.dataset.origInlinePos === '') el.style.removeProperty('position');
          else el.style.position = el.dataset.origInlinePos || 'sticky';
          delete el.dataset.stickyRemoved;
          delete el.dataset.origInlinePos;
        }
      });
    };
  }, [selector, active, overridePosition, useImportant]);
}
